import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import styled from '@emotion/styled';
import * as $trinityEdge from '@/auth/trinity-edge';
import { ActivityIndicator } from '@trinity/chat-components/components/presentation/ActivityIndicator.js';
import trinityLogo from '@/static/logos/logo-trinity.svg?url';
import { actions } from '@/store/actions';
import { useAppDispatch } from '@/store';

type TrinityEdgeSearchParams = {
  sessionId?: string;
  error?: string;
  error_description?: string;
};

type PageError = {
  error: string;
  description?: string;
};

export const TrinityEdgeAuthCallback = () => {
  const location = useLocation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [pageError, setPageError] = useState<PageError | null>(null);

  useEffect(() => {
    // Parse URL search parameters
    const searchParams = new URLSearchParams(location.search);
    const params: TrinityEdgeSearchParams = {
      sessionId: searchParams.get('sessionId') || undefined,
      error: searchParams.get('error') || undefined,
      error_description: searchParams.get('error_description') || undefined,
    };

    // Check for error in URL parameters first
    if (params.error) {
      setPageError({
        error: params.error,
        description: params.error_description,
      });
      return;
    }

    // Handle Trinity Edge callback
    if (params.sessionId) {
      const fingerprint = $trinityEdge.getOrCreateTrinityEdgeFingerprint();

      $trinityEdge.callback({
        sessionId: params.sessionId,
        fingerprint,
      })
        .then(data => {
          if (data.type === 'redirect') {
            dispatch(actions.authChange());
            // Redirect to the return URL or home
            navigate(data.returnUrl || '/home', { replace: true });
          }
        })
        .catch((err: unknown) => {
          console.error('Trinity Edge callback error:', err);
          const error = err as { response?: { status?: number; data?: { message?: string } }; message?: string };
          setPageError({
            error: error?.response?.status?.toString() || 'Authentication Failed',
            description: error?.response?.data?.message || error?.message || 'Unknown error occurred',
          });
        });
      return;
    }

    // If we get here, the parameters are invalid
    setPageError({
      error: 'Invalid Callback',
      description: 'Trinity Edge callback parameters are missing or invalid.',
    });
  }, [location.search, navigate]);

  const renderBody = () => {
    if (pageError) {
      return (
        <ErrorWrapper>
          <ErrorHeader>We're sorry, something went wrong.</ErrorHeader>
          <ErrorLabel>Code:</ErrorLabel>
          <ErrorLine>{pageError.error || 'N/A'}</ErrorLine>
          {pageError.description && (
            <>
              <ErrorLabel>Description:</ErrorLabel>
              <ErrorLine>{pageError.description}</ErrorLine>
            </>
          )}
        </ErrorWrapper>
      );
    }

    return (
      <>
        <ActivityIndicator />
      </>
    );
  };

  return (
    <Root>
      <ContentWrapper>
        <LogoWrapper>
          <TrinityLogo src={trinityLogo} alt="Trinity" />
        </LogoWrapper>
        <Body>
          {renderBody()}
        </Body>
      </ContentWrapper>
    </Root>
  );
};

const Root = styled.div({
  display: 'flex',
  justifyContent: 'center',
  flexDirection: 'column',
  alignItems: 'center',
  height: '80vh',
});

const ContentWrapper = styled.div({
  borderRadius: '10px',
  backgroundColor: 'var(--gray-xxl)',
  padding: '32px',
  minWidth: '400px',
  maxWidth: '800px',
});

const LogoWrapper = styled.div({
  display: 'flex',
  justifyContent: 'center',
  marginBottom: 28,
});

const TrinityLogo = styled.img({
  height: 40,
  width: 'auto',
});

const Body = styled.div({
  marginTop: '20px',
});

const ErrorWrapper = styled.div({
  color: 'var(--black)',
  backgroundColor: 'var(--reddish-white)',
  borderRadius: '15px',
  padding: '32px',
  fontSize: '16px',
});

const ErrorHeader = styled.h1({
  fontSize: '20px',
  fontFamily: 'var(--font-bold)',
  lineHeight: '28px',
  margin: '0 0 30px 0',
});

const ErrorLabel = styled.div({
  fontSize: '16px',
  fontFamily: 'var(--font-semibold)',
  margin: '0 0 4px 0',
});

const ErrorLine = styled.div({
  fontSize: '16px',
  lineHeight: '18px',

  ':not(:last-child)': {
    margin: '0 0 20px 0',
  },
});

const LoginLink = styled.div({
  marginTop: 25,
  '& a': {
    color: 'var(--pri-02)',
    textDecoration: 'none',
    cursor: 'pointer',
    '&:hover': {
      textDecoration: 'underline',
    },
  },
});